import type { ModelProps, ModelRegistry } from '../types';

// ============================================
// CHRISTMAS TOWN - REAL WORLD
// ============================================

export function ChristmasTreeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const treeColor = world === 'real' ? '#0a5a0a' : '#1a4a3a';

  return (
    <group>
      {/* Tree trunk */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.2, 0.6, 8]} />
        <meshStandardMaterial color="#4a2a1a" roughness={0.9} />
      </mesh>
      {/* Tree layers */}
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[0, 0.8 + i * 0.5, 0]} castShadow>
          <coneGeometry args={[0.8 - i * 0.15, 0.7, 8]} />
          <meshStandardMaterial color={treeColor} roughness={0.85} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      {/* Star on top */}
      <mesh position={[0, 2.9, 0]}>
        <octahedronGeometry args={[0.15]} />
        <meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={0.8} metalness={0.7} />
      </mesh>
      {/* Ornaments */}
      {[
        [0.3, 1, 0.3, '#ff0000'], [-0.35, 1.2, 0.2, '#0000ff'], [0.2, 1.5, -0.3, '#FFD700'],
        [-0.25, 1.8, 0.25, '#ff00ff'], [0.3, 2.1, -0.2, '#00ff00'], [-0.2, 2.3, 0.15, '#ff6600'],
      ].map(([x, y, z, color], i) => (
        <mesh key={i} position={[x as number, y as number, z as number]}>
          <sphereGeometry args={[0.08, 10, 10]} />
          <meshStandardMaterial
            color={color as string}
            emissive={color as string}
            emissiveIntensity={0.3}
            metalness={0.5}
          />
        </mesh>
      ))}
      {/* Garland lights */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i / 8) * Math.PI * 2;
        const y = 1 + i * 0.2;
        const r = 0.5 - i * 0.05;
        return (
          <mesh key={`light-${i}`} position={[Math.cos(angle) * r, y, Math.sin(angle) * r]}>
            <sphereGeometry args={[0.03, 6, 6]} />
            <meshStandardMaterial
              color={['#ff0000', '#00ff00', '#ffff00', '#0000ff', '#ff00ff', '#00ffff', '#ff6600', '#ffffff'][i]}
              emissive={['#ff0000', '#00ff00', '#ffff00', '#0000ff', '#ff00ff', '#00ffff', '#ff6600', '#ffffff'][i]}
              emissiveIntensity={0.8}
            />
          </mesh>
        );
      })}
      <pointLight position={[0, 1.5, 0]} color="#FFD700" intensity={2} distance={5} />
    </group>
  );
}

export function GingerbreadHouseModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const wallColor = world === 'real' ? '#8B4513' : '#5a3a2a';

  return (
    <group>
      {/* Main house body */}
      <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[1, 1, 0.8]} />
        <meshStandardMaterial color={wallColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Roof */}
      <mesh position={[0, 1.2, 0]} rotation={[0, 0, 0]} castShadow>
        <coneGeometry args={[0.8, 0.6, 4]} />
        <meshStandardMaterial color="#f5f5f5" roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Frosting trim */}
      <mesh position={[0, 1, 0.41]}>
        <boxGeometry args={[1, 0.1, 0.02]} />
        <meshStandardMaterial color="#ffffff" roughness={0.5} />
      </mesh>
      <mesh position={[0, 1, -0.41]}>
        <boxGeometry args={[1, 0.1, 0.02]} />
        <meshStandardMaterial color="#ffffff" roughness={0.5} />
      </mesh>
      {/* Door */}
      <mesh position={[0, 0.35, 0.41]}>
        <boxGeometry args={[0.25, 0.5, 0.02]} />
        <meshStandardMaterial color="#ff6b6b" />
      </mesh>
      {/* Windows */}
      {[[-0.25, 0.6], [0.25, 0.6]].map(([x, y], i) => (
        <mesh key={i} position={[x, y, 0.41]}>
          <boxGeometry args={[0.15, 0.15, 0.02]} />
          <meshStandardMaterial color="#ffff88" emissive="#ffff44" emissiveIntensity={0.5} />
        </mesh>
      ))}
      {/* Candy decorations */}
      {[[0.35, 0.3, 0.35], [-0.35, 0.4, 0.35], [0.4, 0.7, 0.3]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <sphereGeometry args={[0.08, 8, 8]} />
          <meshStandardMaterial color={['#ff0000', '#00ff00', '#ffff00'][i]} emissive={['#aa0000', '#00aa00', '#aaaa00'][i]} emissiveIntensity={0.3} />
        </mesh>
      ))}
      {/* Warm light from inside */}
      <pointLight position={[0, 0.5, 0.3]} color="#ffaa44" intensity={1.5} distance={2} />
    </group>
  );
}

export function ChristmasCottageModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const woodColor = world === 'real' ? '#5a3a2a' : '#4a2a2a';

  return (
    <group>
      {/* House body */}
      <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.2, 1, 0.9]} />
        <meshStandardMaterial color={woodColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Roof with snow */}
      <mesh position={[0, 1.3, 0]} rotation={[0, Math.PI / 4, 0]} castShadow>
        <coneGeometry args={[0.95, 0.7, 4]} />
        <meshStandardMaterial color="#f0f8ff" roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Snow on roof edges */}
      <mesh position={[0, 1.05, 0.5]}>
        <boxGeometry args={[1.3, 0.1, 0.15]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
      {/* Chimney */}
      <mesh position={[0.35, 1.4, -0.2]} castShadow>
        <boxGeometry args={[0.2, 0.5, 0.2]} />
        <meshStandardMaterial color="#8B0000" roughness={0.9} />
      </mesh>
      {/* Smoke */}
      {[0, 0.1, 0.2].map((y, i) => (
        <mesh key={i} position={[0.35, 1.7 + y, -0.2]}>
          <sphereGeometry args={[0.06 - i * 0.01, 6, 6]} />
          <meshStandardMaterial color="#cccccc" transparent opacity={0.5 - i * 0.1} />
        </mesh>
      ))}
      {/* Door */}
      <mesh position={[0, 0.4, 0.46]}>
        <boxGeometry args={[0.3, 0.7, 0.02]} />
        <meshStandardMaterial color="#228B22" />
      </mesh>
      {/* Wreath */}
      <mesh position={[0, 0.55, 0.48]}>
        <torusGeometry args={[0.08, 0.02, 8, 16]} />
        <meshStandardMaterial color="#228B22" />
      </mesh>
      {/* Windows with warm glow */}
      {[[-0.35, 0.55], [0.35, 0.55]].map(([x, y], i) => (
        <group key={i}>
          <mesh position={[x, y, 0.46]}>
            <boxGeometry args={[0.2, 0.25, 0.02]} />
            <meshStandardMaterial color="#ffee88" emissive="#ffdd44" emissiveIntensity={0.6} />
          </mesh>
          <pointLight position={[x, y, 0.5]} color="#ffaa44" intensity={0.8} distance={1.5} />
        </group>
      ))}
    </group>
  );
}

export function SantaWorkshopModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Main building */}
      <mesh position={[0, 0.8, 0]} castShadow receiveShadow>
        <boxGeometry args={[2, 1.6, 1.5]} />
        <meshStandardMaterial color="#8B0000" roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Roof */}
      <mesh position={[0, 1.9, 0]} castShadow>
        <boxGeometry args={[2.2, 0.15, 1.7]} />
        <meshStandardMaterial color="#f0f8ff" roughness={0.9} />
      </mesh>
      <mesh position={[0, 2.2, 0]} rotation={[0, 0, 0]} castShadow>
        <boxGeometry args={[2.3, 0.5, 1.8]} />
        <meshStandardMaterial color="#228B22" roughness={0.85} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Large window */}
      <mesh position={[0, 0.9, 0.76]}>
        <boxGeometry args={[1, 0.8, 0.02]} />
        <meshStandardMaterial color="#ffee88" emissive="#ffdd44" emissiveIntensity={0.5} />
      </mesh>
      {/* Door */}
      <mesh position={[-0.6, 0.5, 0.76]}>
        <boxGeometry args={[0.4, 1, 0.02]} />
        <meshStandardMaterial color="#228B22" />
      </mesh>
      {/* "TOYS" sign */}
      <mesh position={[0.5, 1.5, 0.77]}>
        <boxGeometry args={[0.5, 0.2, 0.02]} />
        <meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={0.3} />
      </mesh>
      {/* Candy cane pillars */}
      {[[-0.9, 0.75], [0.9, 0.75]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          {[0, 1, 2, 3, 4, 5].map((j) => (
            <mesh key={j} position={[0, 0.15 + j * 0.25, 0]}>
              <cylinderGeometry args={[0.08, 0.08, 0.15, 8]} />
              <meshStandardMaterial color={j % 2 === 0 ? '#ff0000' : '#ffffff'} />
            </mesh>
          ))}
        </group>
      ))}
      {/* Lights along roof */}
      {[-0.8, -0.4, 0, 0.4, 0.8].map((x, i) => (
        <mesh key={i} position={[x, 1.65, 0.8]}>
          <sphereGeometry args={[0.05, 8, 8]} />
          <meshStandardMaterial
            color={['#ff0000', '#00ff00', '#ffff00', '#0000ff', '#ff00ff'][i]}
            emissive={['#ff0000', '#00ff00', '#ffff00', '#0000ff', '#ff00ff'][i]}
            emissiveIntensity={0.8}
          />
        </mesh>
      ))}
      <pointLight position={[0, 1, 0.8]} color="#ffaa44" intensity={3} distance={4} />
    </group>
  );
}

export function ChristmasLampModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Pole */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 1.6, 8]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.6} roughness={0.4} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Lamp housing */}
      <mesh position={[0, 1.7, 0]} castShadow>
        <boxGeometry args={[0.2, 0.25, 0.2]} />
        <meshStandardMaterial color="#2a2a2a" metalness={0.5} roughness={0.5} />
      </mesh>
      {/* Glass panels */}
      {[[0.11, 0], [-0.11, 0], [0, 0.11], [0, -0.11]].map(([x, z], i) => (
        <mesh key={i} position={[x, 1.7, z]}>
          <boxGeometry args={[x !== 0 ? 0.02 : 0.15, 0.2, z !== 0 ? 0.02 : 0.15]} />
          <meshStandardMaterial color="#ffee88" emissive="#ffdd44" emissiveIntensity={0.8} transparent opacity={0.9} />
        </mesh>
      ))}
      {/* Roof */}
      <mesh position={[0, 1.9, 0]} castShadow>
        <coneGeometry args={[0.15, 0.15, 4]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.6} />
      </mesh>
      {/* Wreath decoration */}
      <mesh position={[0, 1.4, 0.06]}>
        <torusGeometry args={[0.06, 0.015, 8, 16]} />
        <meshStandardMaterial color="#228B22" />
      </mesh>
      {/* Red bow */}
      <mesh position={[0, 1.33, 0.07]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#ff0000" />
      </mesh>
      <pointLight position={[0, 1.7, 0]} color="#ffdd88" intensity={3} distance={4} />
    </group>
  );
}

export function SnowmanModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Bottom sphere */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <sphereGeometry args={[0.35, 16, 16]} />
        <meshStandardMaterial color="#f8f8ff" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Middle sphere */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <sphereGeometry args={[0.25, 14, 14]} />
        <meshStandardMaterial color="#f8f8ff" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 1.2, 0]} castShadow>
        <sphereGeometry args={[0.18, 12, 12]} />
        <meshStandardMaterial color="#f8f8ff" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Top hat */}
      <mesh position={[0, 1.45, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.12, 0.2, 12]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.35, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.18, 0.03, 12]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
      </mesh>
      {/* Carrot nose */}
      <mesh position={[0, 1.2, 0.2]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <coneGeometry args={[0.03, 0.15, 6]} />
        <meshStandardMaterial color="#ff6600" roughness={0.8} />
      </mesh>
      {/* Coal eyes */}
      {[-0.06, 0.06].map((x, i) => (
        <mesh key={i} position={[x, 1.25, 0.15]}>
          <sphereGeometry args={[0.025, 8, 8]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}
      {/* Coal mouth */}
      {[-0.05, 0, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 1.12, 0.16]}>
          <sphereGeometry args={[0.015, 6, 6]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}
      {/* Coal buttons */}
      {[0.75, 0.85, 0.95].map((y, i) => (
        <mesh key={i} position={[0, y, 0.22]}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshStandardMaterial color="#1a1a1a" />
        </mesh>
      ))}
      {/* Stick arms */}
      {[-1, 1].map((side, i) => (
        <mesh key={i} position={[side * 0.35, 0.85, 0]} rotation={[0, 0, side * 0.5]} castShadow>
          <cylinderGeometry args={[0.015, 0.01, 0.3, 5]} />
          <meshStandardMaterial color="#4a3a2a" roughness={0.95} />
        </mesh>
      ))}
      {/* Scarf */}
      <mesh position={[0, 1.02, 0]}>
        <torusGeometry args={[0.2, 0.03, 8, 16]} />
        <meshStandardMaterial color="#ff0000" />
      </mesh>
    </group>
  );
}

export function GiftPileModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const gifts = [
    { pos: [0, 0.1, 0], size: [0.3, 0.2, 0.25], color: '#ff0000', ribbon: '#FFD700' },
    { pos: [0.2, 0.15, 0.15], size: [0.25, 0.3, 0.2], color: '#00aa00', ribbon: '#ff0000' },
    { pos: [-0.15, 0.1, 0.2], size: [0.2, 0.2, 0.2], color: '#0000ff', ribbon: '#ffffff' },
    { pos: [0.1, 0.35, 0.05], size: [0.18, 0.15, 0.18], color: '#ff00ff', ribbon: '#00ff00' },
  ];

  return (
    <group>
      {gifts.map((gift, i) => (
        <group key={i} position={gift.pos as [number, number, number]}>
          {/* Box */}
          <mesh castShadow>
            <boxGeometry args={gift.size as [number, number, number]} />
            <meshStandardMaterial color={gift.color} roughness={0.6} transparent={isOccluding} opacity={opacity} />
          </mesh>
          {/* Ribbon horizontal */}
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[gift.size[0] + 0.01, 0.03, gift.size[2] + 0.01]} />
            <meshStandardMaterial color={gift.ribbon} metalness={0.3} />
          </mesh>
          {/* Ribbon vertical */}
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.03, gift.size[1] + 0.01, gift.size[2] + 0.01]} />
            <meshStandardMaterial color={gift.ribbon} metalness={0.3} />
          </mesh>
          {/* Bow */}
          <mesh position={[0, gift.size[1] / 2 + 0.03, 0]}>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshStandardMaterial color={gift.ribbon} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

export function SantaSleighModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Sleigh body */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[1.2, 0.4, 0.6]} />
        <meshStandardMaterial color="#8B0000" roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Back rest */}
      <mesh position={[-0.4, 0.6, 0]} castShadow>
        <boxGeometry args={[0.1, 0.5, 0.6]} />
        <meshStandardMaterial color="#8B0000" roughness={0.7} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Front curve */}
      <mesh position={[0.55, 0.35, 0]} rotation={[0, 0, -0.3]} castShadow>
        <boxGeometry args={[0.3, 0.1, 0.55]} />
        <meshStandardMaterial color="#8B0000" roughness={0.7} />
      </mesh>
      {/* Runners */}
      {[-0.25, 0.25].map((z, i) => (
        <group key={i} position={[0, 0, z]}>
          <mesh position={[0, 0.05, 0]} castShadow>
            <boxGeometry args={[1.4, 0.05, 0.05]} />
            <meshStandardMaterial color="#FFD700" metalness={0.7} roughness={0.3} />
          </mesh>
          {/* Curved front */}
          <mesh position={[0.7, 0.15, 0]} rotation={[0, 0, -0.8]} castShadow>
            <boxGeometry args={[0.25, 0.05, 0.05]} />
            <meshStandardMaterial color="#FFD700" metalness={0.7} roughness={0.3} />
          </mesh>
        </group>
      ))}
      {/* Gold trim */}
      <mesh position={[0, 0.52, 0]}>
        <boxGeometry args={[1.25, 0.03, 0.65]} />
        <meshStandardMaterial color="#FFD700" metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Gift sack */}
      <mesh position={[0.1, 0.6, 0]} castShadow>
        <sphereGeometry args={[0.3, 10, 10]} />
        <meshStandardMaterial color="#8B4513" roughness={0.9} />
      </mesh>
    </group>
  );
}

export function SantaHatModel({ world }: ModelProps) {
  return (
    <group rotation={[0.3, 0, 0.2]}>
      {/* Main hat cone */}
      <mesh castShadow>
        <coneGeometry args={[0.15, 0.35, 12]} />
        <meshStandardMaterial color="#ff0000" roughness={0.7} />
      </mesh>
      {/* White trim at base */}
      <mesh position={[0, -0.12, 0]}>
        <torusGeometry args={[0.15, 0.04, 8, 16]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
      {/* Pom pom */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <sphereGeometry args={[0.06, 10, 10]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
    </group>
  );
}

// ============================================
// HALLOWEEN TOWN - SPECTRAL WORLD
// ============================================

export function SpiralHillModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Hill base */}
      <mesh position={[0, 0.4, 0]} castShadow receiveShadow>
        <coneGeometry args={[1, 0.8, 16]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#2a2a3a' : '#4a4a5a'}
          roughness={0.95}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Spiral path */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i / 8) * Math.PI * 3;
        const r = 0.8 - i * 0.08;
        const y = 0.2 + i * 0.15;
        return (
          <mesh key={i} position={[Math.cos(angle) * r, y, Math.sin(angle) * r]} rotation={[0, -angle, Math.PI / 6]}>
            <boxGeometry args={[0.3, 0.05, 0.15]} />
            <meshStandardMaterial color="#3a3a4a" roughness={0.9} />
          </mesh>
        );
      })}
      {/* Moon behind (decorative) */}
      {world === 'spectral' && (
        <mesh position={[0, 2, -1]}>
          <circleGeometry args={[0.5, 24]} />
          <meshStandardMaterial color="#ffffcc" emissive="#ffff88" emissiveIntensity={0.5} />
        </mesh>
      )}
      {/* Glow at top */}
      {world === 'spectral' && (
        <pointLight position={[0, 1.4, 0]} color="#ff6600" intensity={1} distance={3} />
      )}
    </group>
  );
}

export function JackTowerModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const towerColor = world === 'spectral' ? '#2a2a3a' : '#3a3a4a';

  return (
    <group>
      {/* Main tower - twisted */}
      <mesh position={[0, 1.2, 0]} rotation={[0, 0, 0.1]} castShadow>
        <cylinderGeometry args={[0.3, 0.5, 2.4, 8]} />
        <meshStandardMaterial color={towerColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Crooked roof */}
      <mesh position={[0.1, 2.6, 0]} rotation={[0.1, 0, 0.15]} castShadow>
        <coneGeometry args={[0.45, 0.8, 8]} />
        <meshStandardMaterial color="#1a1a2a" roughness={0.85} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Windows (glowing) */}
      {[[0, 1.8, 0.31], [0, 1.2, 0.35], [0, 0.6, 0.4]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <boxGeometry args={[0.15, 0.2, 0.02]} />
          <meshStandardMaterial
            color="#ff6600"
            emissive="#ff4400"
            emissiveIntensity={0.8}
          />
        </mesh>
      ))}
      {/* Crooked fence around */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.7, 0.2, Math.sin(angle) * 0.7]} rotation={[0, angle, 0.1 * (i % 2 === 0 ? 1 : -1)]}>
            <boxGeometry args={[0.03, 0.4, 0.03]} />
            <meshStandardMaterial color="#2a2a2a" roughness={0.95} />
          </mesh>
        );
      })}
      <pointLight position={[0, 1.2, 0.5]} color="#ff6600" intensity={2} distance={3} />
    </group>
  );
}

export function GiantPumpkinModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const pumpkinColor = world === 'spectral' ? '#cc5500' : '#ff6600';

  return (
    <group>
      {/* Main pumpkin body */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <sphereGeometry args={[0.5, 12, 12]} />
        <meshStandardMaterial
          color={pumpkinColor}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Pumpkin ridges */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i / 8) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.48, 0.4, Math.sin(angle) * 0.48]} rotation={[0, angle + Math.PI / 2, 0]}>
            <capsuleGeometry args={[0.08, 0.7, 6, 8]} />
            <meshStandardMaterial color={world === 'spectral' ? '#aa4400' : '#dd5500'} roughness={0.85} />
          </mesh>
        );
      })}
      {/* Stem */}
      <mesh position={[0, 0.9, 0]} rotation={[0, 0, 0.2]} castShadow>
        <cylinderGeometry args={[0.05, 0.08, 0.2, 6]} />
        <meshStandardMaterial color="#228B22" roughness={0.9} />
      </mesh>
    </group>
  );
}

export function JackOLanternModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Pumpkin body */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <sphereGeometry args={[0.4, 12, 12]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#cc5500' : '#ff6600'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Carved eyes (triangles made with boxes) */}
      {[-0.12, 0.12].map((x, i) => (
        <mesh key={i} position={[x, 0.45, 0.35]} rotation={[0, 0, i === 0 ? 0.3 : -0.3]}>
          <boxGeometry args={[0.1, 0.08, 0.1]} />
          <meshStandardMaterial
            color="#000000"
            emissive={world === 'spectral' ? '#ff6600' : '#ffaa00'}
            emissiveIntensity={world === 'spectral' ? 1 : 0.5}
          />
        </mesh>
      ))}
      {/* Carved nose */}
      <mesh position={[0, 0.35, 0.38]}>
        <boxGeometry args={[0.06, 0.08, 0.1]} />
        <meshStandardMaterial color="#000000" emissive={world === 'spectral' ? '#ff6600' : '#ffaa00'} emissiveIntensity={world === 'spectral' ? 1 : 0.5} />
      </mesh>
      {/* Carved mouth */}
      <mesh position={[0, 0.22, 0.36]}>
        <boxGeometry args={[0.2, 0.06, 0.1]} />
        <meshStandardMaterial color="#000000" emissive={world === 'spectral' ? '#ff6600' : '#ffaa00'} emissiveIntensity={world === 'spectral' ? 1 : 0.5} />
      </mesh>
      {/* Stem */}
      <mesh position={[0, 0.75, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.06, 0.15, 6]} />
        <meshStandardMaterial color="#228B22" roughness={0.9} />
      </mesh>
      {/* Inner glow */}
      <pointLight position={[0, 0.35, 0.2]} color="#ff6600" intensity={2} distance={2} />
    </group>
  );
}

export function SpookyTreeModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const trunkColor = world === 'spectral' ? '#1a1a2a' : '#2a2a3a';

  return (
    <group>
      {/* Twisted trunk */}
      <mesh position={[0, 0.6, 0]} rotation={[0, 0, 0.1]} castShadow>
        <cylinderGeometry args={[0.1, 0.2, 1.2, 6]} />
        <meshStandardMaterial color={trunkColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Gnarled branches */}
      {[
        { pos: [0.2, 1, 0.1], rot: [0.3, 0, 0.8], len: 0.5 },
        { pos: [-0.15, 1.1, -0.05], rot: [-0.2, 0, -0.7], len: 0.45 },
        { pos: [0.1, 0.8, -0.15], rot: [-0.4, 0.3, 0.5], len: 0.35 },
        { pos: [-0.1, 0.9, 0.12], rot: [0.5, -0.2, -0.6], len: 0.4 },
      ].map((branch, i) => (
        <mesh key={i} position={branch.pos as [number, number, number]} rotation={branch.rot as [number, number, number]} castShadow>
          <cylinderGeometry args={[0.02, 0.04, branch.len, 5]} />
          <meshStandardMaterial color={trunkColor} roughness={0.95} />
        </mesh>
      ))}
      {/* Finger-like twigs */}
      {[0.35, -0.3, 0.25, -0.25].map((x, i) => (
        <mesh key={i} position={[x, 1.2 + i * 0.05, (i % 2) * 0.1]} rotation={[0.2, 0, x > 0 ? 0.5 : -0.5]} castShadow>
          <cylinderGeometry args={[0.01, 0.02, 0.2, 4]} />
          <meshStandardMaterial color={trunkColor} roughness={0.95} />
        </mesh>
      ))}
    </group>
  );
}

export function CreepyFountainModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Basin */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.6, 0.5, 0.4, 12]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#2a2a3a' : '#4a4a5a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Center pillar */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 0.8, 8]} />
        <meshStandardMaterial color={world === 'spectral' ? '#3a3a4a' : '#5a5a6a'} roughness={0.85} />
      </mesh>
      {/* Skull top */}
      <mesh position={[0, 1.1, 0]} castShadow>
        <sphereGeometry args={[0.15, 10, 10]} />
        <meshStandardMaterial color="#e8e8d8" roughness={0.8} />
      </mesh>
      {/* Eye sockets */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 1.12, 0.12]}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshStandardMaterial color="#000000" />
        </mesh>
      ))}
      {/* Green slime liquid */}
      <mesh position={[0, 0.25, 0]}>
        <cylinderGeometry args={[0.55, 0.55, 0.1, 12]} />
        <meshStandardMaterial
          color="#00ff00"
          emissive="#00aa00"
          emissiveIntensity={0.5}
          transparent
          opacity={0.8}
        />
      </mesh>
      {/* Dripping slime */}
      {[0.3, -0.25, 0.15].map((x, i) => (
        <mesh key={i} position={[x, 0.15, 0.3 - i * 0.15]}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshStandardMaterial color="#00ff00" emissive="#00aa00" emissiveIntensity={0.8} />
        </mesh>
      ))}
      <pointLight position={[0, 0.3, 0]} color="#00ff00" intensity={2} distance={3} />
    </group>
  );
}

export function GuillotineModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const woodColor = world === 'spectral' ? '#2a1a1a' : '#4a3a2a';

  return (
    <group>
      {/* Frame posts */}
      {[-0.3, 0.3].map((x, i) => (
        <mesh key={i} position={[x, 0.8, 0]} castShadow>
          <boxGeometry args={[0.1, 1.6, 0.1]} />
          <meshStandardMaterial color={woodColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      {/* Top beam */}
      <mesh position={[0, 1.65, 0]} castShadow>
        <boxGeometry args={[0.75, 0.1, 0.1]} />
        <meshStandardMaterial color={woodColor} roughness={0.9} />
      </mesh>
      {/* Blade */}
      <mesh position={[0, 1.3, 0]} castShadow>
        <boxGeometry args={[0.5, 0.02, 0.08]} />
        <meshStandardMaterial color="#a0a0a0" metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Angled blade edge */}
      <mesh position={[0, 1.28, 0.03]} rotation={[0.3, 0, 0]} castShadow>
        <boxGeometry args={[0.5, 0.02, 0.04]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.9} roughness={0.1} />
      </mesh>
      {/* Base platform */}
      <mesh position={[0, 0.05, 0]} castShadow>
        <boxGeometry args={[0.8, 0.1, 0.6]} />
        <meshStandardMaterial color={woodColor} roughness={0.9} />
      </mesh>
      {/* Neck hole */}
      <mesh position={[0, 0.15, 0.2]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.1, 0.03, 8, 12]} />
        <meshStandardMaterial color={woodColor} roughness={0.9} />
      </mesh>
    </group>
  );
}

export function CoffinModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const woodColor = world === 'spectral' ? '#2a1a1a' : '#3a2a1a';

  return (
    <group>
      {/* Coffin body - hexagonal shape */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <boxGeometry args={[0.4, 0.25, 1]} />
        <meshStandardMaterial color={woodColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Lid */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[0.42, 0.05, 1.02]} />
        <meshStandardMaterial color={woodColor} roughness={0.85} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Cross on top */}
      <mesh position={[0, 0.34, 0]}>
        <boxGeometry args={[0.03, 0.02, 0.2]} />
        <meshStandardMaterial color="#4a4a4a" />
      </mesh>
      <mesh position={[0, 0.34, 0.03]}>
        <boxGeometry args={[0.1, 0.02, 0.03]} />
        <meshStandardMaterial color="#4a4a4a" />
      </mesh>
    </group>
  );
}

export function CoffinOpenModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const woodColor = world === 'spectral' ? '#2a1a1a' : '#3a2a1a';

  return (
    <group>
      {/* Coffin body */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <boxGeometry args={[0.4, 0.25, 1]} />
        <meshStandardMaterial color={woodColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Inside (dark) */}
      <mesh position={[0, 0.2, 0]}>
        <boxGeometry args={[0.35, 0.15, 0.95]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
      {/* Lid (open/leaning) */}
      <mesh position={[0.35, 0.4, 0]} rotation={[0, 0, 0.8]} castShadow>
        <boxGeometry args={[0.42, 0.05, 1.02]} />
        <meshStandardMaterial color={woodColor} roughness={0.85} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Creepy hand reaching out */}
      {world === 'spectral' && (
        <mesh position={[0, 0.35, 0.2]} rotation={[0.3, 0, 0.2]}>
          <capsuleGeometry args={[0.02, 0.1, 4, 8]} />
          <meshStandardMaterial color="#a0a090" roughness={0.9} />
        </mesh>
      )}
    </group>
  );
}

export function TombstoneModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const stoneColor = world === 'spectral' ? '#4a4a5a' : '#6a6a7a';

  return (
    <group>
      {/* Stone slab */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[0.5, 0.7, 0.1]} />
        <meshStandardMaterial color={stoneColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Rounded top */}
      <mesh position={[0, 0.75, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.25, 0.1, 12, 1, false, 0, Math.PI]} />
        <meshStandardMaterial color={stoneColor} roughness={0.95} />
      </mesh>
      {/* RIP text area */}
      <mesh position={[0, 0.5, 0.06]}>
        <boxGeometry args={[0.3, 0.2, 0.01]} />
        <meshStandardMaterial color={world === 'spectral' ? '#3a3a4a' : '#5a5a6a'} />
      </mesh>
      {/* Crack */}
      <mesh position={[0.1, 0.3, 0.06]} rotation={[0, 0, 0.5]}>
        <boxGeometry args={[0.02, 0.3, 0.01]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh>
    </group>
  );
}

export function HolidayTreeDoorsModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const treeColor = world === 'spectral' ? '#1a3a1a' : '#2a5a2a';

  return (
    <group>
      {/* Three trees with doors */}
      {[
        { x: -0.8, door: '#ff0000', symbol: 'heart' }, // Valentine
        { x: 0, door: '#ff6600', symbol: 'pumpkin' },  // Halloween
        { x: 0.8, door: '#00aa00', symbol: 'tree' },   // Christmas
      ].map((tree, i) => (
        <group key={i} position={[tree.x, 0, 0]}>
          {/* Tree trunk */}
          <mesh position={[0, 0.8, 0]} castShadow>
            <cylinderGeometry args={[0.15, 0.2, 1.6, 8]} />
            <meshStandardMaterial color={treeColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
          </mesh>
          {/* Door on trunk */}
          <mesh position={[0, 0.6, 0.16]}>
            <boxGeometry args={[0.2, 0.35, 0.02]} />
            <meshStandardMaterial
              color={tree.door}
              emissive={tree.door}
              emissiveIntensity={0.5}
            />
          </mesh>
          {/* Door handle */}
          <mesh position={[0.06, 0.58, 0.18]}>
            <sphereGeometry args={[0.02, 6, 6]} />
            <meshStandardMaterial color="#FFD700" metalness={0.7} />
          </mesh>
          {/* Branches */}
          {[0.5, 0.8, 1.1].map((y, j) => (
            <mesh key={j} position={[(j % 2 === 0 ? 0.15 : -0.15), y, 0]} rotation={[0, 0, (j % 2 === 0 ? 0.5 : -0.5)]} castShadow>
              <cylinderGeometry args={[0.02, 0.04, 0.25, 5]} />
              <meshStandardMaterial color={treeColor} roughness={0.9} />
            </mesh>
          ))}
        </group>
      ))}
      {/* Ground mound */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <boxGeometry args={[2.5, 0.2, 0.8]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.95} />
      </mesh>
    </group>
  );
}

export function SoulRobberModel({ world }: ModelProps) {
  return (
    <group>
      {/* Handle */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.025, 0.3, 8]} />
        <meshStandardMaterial
          color="#2a2a2a"
          roughness={0.8}
        />
      </mesh>
      {/* Ghost-like shape at top */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <coneGeometry args={[0.08, 0.15, 8]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#88ff88' : '#aaffaa'}
          emissive="#44ff44"
          emissiveIntensity={0.5}
          transparent
          opacity={0.8}
        />
      </mesh>
      {/* Wisps */}
      {[0, 1, 2].map((i) => {
        const angle = (i / 3) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.1, 0.4 + i * 0.03, Math.sin(angle) * 0.1]}>
            <sphereGeometry args={[0.02, 6, 6]} />
            <meshStandardMaterial color="#88ff88" emissive="#44ff44" emissiveIntensity={1} transparent opacity={0.6} />
          </mesh>
        );
      })}
      {/* Glow */}
      {world === 'spectral' && (
        <pointLight position={[0, 0.35, 0]} color="#88ff88" intensity={1.5} distance={1.5} />
      )}
    </group>
  );
}

// Registry of all Nightmare Before Christmas models
export const nightmareChristmasModels: ModelRegistry = {
  // Christmas Town (Real)
  christmas_tree: ChristmasTreeModel,
  gingerbread_house: GingerbreadHouseModel,
  christmas_cottage: ChristmasCottageModel,
  santa_workshop: SantaWorkshopModel,
  christmas_lamp: ChristmasLampModel,
  snowman: SnowmanModel,
  gift_pile: GiftPileModel,
  santa_sleigh: SantaSleighModel,
  santa_hat: SantaHatModel,
  // Halloween Town (Spectral)
  spiral_hill: SpiralHillModel,
  jack_tower: JackTowerModel,
  giant_pumpkin: GiantPumpkinModel,
  jack_o_lantern: JackOLanternModel,
  spooky_tree: SpookyTreeModel,
  creepy_fountain: CreepyFountainModel,
  guillotine: GuillotineModel,
  coffin: CoffinModel,
  coffin_open: CoffinOpenModel,
  tombstone: TombstoneModel,
  holiday_tree_doors: HolidayTreeDoorsModel,
  soul_robber: SoulRobberModel,
};
